import type { TestimonialExportRequest, TestimonialFilterParams } from "@/api/pipeline.service";
import type { TaxonomyMode } from "@/api/submissions.service";
import { taxonomyValueFitsMode } from "@/utils/taxonomyFilter";
import { MIN_SEARCH_LENGTH } from "../searchConstants";

const ALL = "all";

export const TESTIMONIAL_AXES = ["publication", "region", "practice"] as const;

export type TestimonialAxis = (typeof TESTIMONIAL_AXES)[number];

export type TestimonialScreenFilters = {
  publication: string;
  region: string;
  practice: string;
  firm: string;
  contact: string;
  type: string;
  language: string;
  sentiment: string;
};

type EffectiveFilterInput = {
  mode: TaxonomyMode;
  taxonomy: TaxonomyMode | undefined;
  filters: TestimonialScreenFilters;
  regionOptions: { value: string; label: string }[];
  noRegionsForPublication: boolean;
  debouncedSearch: string;
};

function selected(value: string): string | undefined {
  return value === ALL ? undefined : value;
}

function legacyPracticeValue(value: string): string {
  return value.split(":")[0];
}

export function axisValueFitsMode(
  axis: TestimonialAxis,
  value: string,
  mode: TaxonomyMode,
): boolean {
  return taxonomyValueFitsMode(axis === "practice" ? legacyPracticeValue(value) : value, mode);
}

export function selectedAxisValue(
  axis: TestimonialAxis,
  value: string,
  mode: TaxonomyMode,
): string | undefined {
  return value !== ALL && axisValueFitsMode(axis, value, mode) ? value : undefined;
}

export function buildEffectiveTestimonialFilters({
  mode,
  taxonomy,
  filters,
  regionOptions,
  noRegionsForPublication,
  debouncedSearch,
}: EffectiveFilterInput): TestimonialFilterParams {
  const publication = selectedAxisValue("publication", filters.publication, mode);
  const region =
    publication === undefined ? undefined : selectedAxisValue("region", filters.region, mode);
  const practice =
    publication !== undefined && (region !== undefined || noRegionsForPublication)
      ? selectedAxisValue("practice", filters.practice, mode)
      : undefined;
  const search = debouncedSearch.trim();
  const common = {
    companyId: filters.firm !== ALL ? Number(filters.firm) : undefined,
    lawyerName: selected(filters.contact),
    testimonialType: selected(filters.type),
    language: selected(filters.language),
    sentiment: selected(filters.sentiment),
    search: search.length >= MIN_SEARCH_LENGTH ? search : undefined,
    taxonomy,
  };

  if (mode === "new") {
    return { editionId: publication, rankingEntityId: region, bpaId: practice, ...common };
  }
  return {
    publicationId: publication === undefined ? undefined : Number.parseInt(publication, 10),
    practiceAreaId: practice === undefined ? undefined : Number.parseInt(practice, 10),
    regionName:
      region === undefined
        ? undefined
        : regionOptions.find(option => option.value === region)?.label,
    ...common,
  };
}

type ExportLabels = {
  visibleOnly: boolean;
  firmName?: string | null;
  publicationName?: string | null;
  edition?: string | null;
};

function isPositiveInteger(value: number | undefined): value is number {
  return value !== undefined && Number.isInteger(value) && value > 0;
}

function label(value: string | null | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export function buildTestimonialExportRequest(
  effective: TestimonialFilterParams,
  { visibleOnly, firmName, publicationName, edition }: ExportLabels,
): TestimonialExportRequest | null {
  const { companyId, publicationId, editionId, taxonomy, testimonialType, ...optional } = effective;
  if (!isPositiveInteger(companyId)) {
    return null;
  }
  const labels = {
    visibleOnly,
    firmName: label(firmName),
    publicationName: label(publicationName),
    edition: label(edition),
  };
  if (taxonomy === "new") {
    return editionId
      ? { ...optional, type: testimonialType, taxonomy, companyId, editionId, ...labels }
      : null;
  }
  if (!isPositiveInteger(publicationId)) {
    return null;
  }
  return { ...optional, type: testimonialType, taxonomy, companyId, publicationId, ...labels };
}
